import { useCallback, useState } from 'react';
import { useDispatch } from 'dva';
import { message } from 'antd';
import { useIntl } from 'umi';

/**
 * Custom hook for handling chat with AutomyAI agents
 * @param {Object} instance - Process instance object containing processInstanceId
 * @param {Object} [options] - Optional configuration
 * @param {string} [options.agentId] - Explicit agent ID; ensures the correct agent is
 *   selected even when the thread API response omits agentId
 * @returns {{ isLoading: Boolean, handleChatWithAgent: Function }}
 */
const useChatWithAgent = (instance, options) => {
  const dispatch = useDispatch();

  const { formatMessage } = useIntl();
  const [isLoading, setLoading] = useState(false);

  const agentId = options?.agentId;

  const handleChatWithAgent = useCallback(async () => {
    try {
      setLoading(true);

      const chatThreadId = instance.processInstanceId;
      if (!chatThreadId) {
        setLoading(false);
        return;
      }

      dispatch({
        type: 'chat/openSpecificChat',
        payload: {
          threadId: chatThreadId,
          agentId,
          onSuccess: () => setLoading(false),
          onError: () => {
            setLoading(false);
            message.error(formatMessage({ id: 'global.error.automyAI.openChat' }));
          },
        },
      });
    } catch (e) {
      setLoading(false);
      message.error(formatMessage({ id: 'global.error.automyAI.openChat' }));
    }
  }, [dispatch, formatMessage, instance, agentId]);

  return { isLoading, handleChatWithAgent };
};

export default useChatWithAgent;
